/* eslint-disable react/no-unknown-property -- react-three/fiber uses JSX props ESLint doesn't recognise */
import React, { useEffect, useMemo, useRef, useState } from "react";import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Grid, GizmoHelper, GizmoViewport, Line, AdaptiveDpr, AdaptiveEvents } from "@react-three/drei";
import * as THREE from "three";
// Iter I-23 · Phase 2 — GPU-accelerated raycasting. Replacing the default
// per-triangle CPU raycaster with a BVH-backed acceleration structure
// drops per-object picks from ~250 ms on 500k blocks to <10 ms.
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
THREE.Mesh.prototype.raycast          = acceleratedRaycast;
THREE.InstancedMesh.prototype.raycast = acceleratedRaycast;
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
import {
  ZoomIn, ZoomOut, RotateCcw, Maximize2, ArrowLeft, ArrowRight,
  Camera, FileText, FileImage,
} from "lucide-react";
import { useWorkspace } from "@/lib/workspace";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";

// Color scale for lithology categories (deterministic)
const LITH_COLORS = ["#FBBF24", "#34D399", "#818CF8", "#F472B6", "#22D3EE", "#4d9c1f", "#A78BFA"];
function litColor(name, cache) {
  if (!name) return "#94A3B8";
  if (cache[name]) return cache[name];
  const idx = Object.keys(cache).length % LITH_COLORS.length;
  cache[name] = LITH_COLORS[idx];
  return cache[name];
}

// Grade color ramp (green -> yellow -> red) — Iter 70 · Feb 2026.
// Low grades render as cool green (low-value), high grades as hot red.
function gradeColor(g, min = 0, max = 2) {
  const t = Math.max(0, Math.min(1, (g - min) / (max - min || 1)));
  const c = new THREE.Color();
  // hue: 0.33 (green) → 0.16 (yellow) → 0.0 (red)
  const hue = 0.33 - t * 0.33;
  c.setHSL(hue, 0.9, 0.5);
  return `#${c.getHexString()}`;
}

// ─── Classification (Measured / Indicated / Inferred) ──────────────────────
// Iter I-38 · P12 — visualise the JORC / NI-43-101 confidence class on the
// 3D block model. Uses the SAME tercile rule the PDF report applies:
//   0–33rd pct of kriging_variance → Measured (highest confidence)
//   33–66th pct                    → Indicated
//   66–100th pct or missing        → Inferred
// Fallback when no `kriging_variance` field is present anywhere: derive
// terciles from `distance_to_nearest_sample` (falls back to `nearest_hole_distance`
// or `distance_m`). If none of those exist either, every block is Inferred.
export const CLASSIFICATION_COLORS = {
  measured:  "#22c55e", // green — highest confidence
  indicated: "#f59e0b", // amber
  inferred:  "#ef4444", // red — lowest confidence
};

function _pickVarianceKey(blocks) {
  const candidates = [
    "kriging_variance", "estimation_variance",
    "distance_to_nearest_sample", "nearest_hole_distance", "distance_m",
  ];
  for (const k of candidates) {
    for (const b of blocks) {
      const v = b?.[k];
      if (typeof v === "number" && Number.isFinite(v)) return k;
    }
  }
  return null;
}

export function computeClassificationTerciles(blocks) {
  const key = _pickVarianceKey(blocks || []);
  const counts = { measured: 0, indicated: 0, inferred: 0 };
  const empty = { key, p33: null, p66: null, counts, total: (blocks?.length || 0) };
  if (!key || !blocks || blocks.length === 0) {
    counts.inferred = blocks?.length || 0;
    return empty;
  }
  const vals = [];
  for (const b of blocks) {
    const v = b?.[key];
    if (typeof v === "number" && Number.isFinite(v)) vals.push(v);
  }
  if (vals.length === 0) {
    counts.inferred = blocks.length;
    return empty;
  }
  vals.sort((a, b) => a - b);
  const p33 = vals[Math.floor(vals.length * 0.33)];
  const p66 = vals[Math.floor(vals.length * 0.66)];
  for (const b of blocks) {
    const v = b?.[key];
    if (typeof v !== "number" || !Number.isFinite(v)) counts.inferred++;
    else if (v <= p33) counts.measured++;
    else if (v <= p66) counts.indicated++;
    else counts.inferred++;
  }
  return { key, p33, p66, counts, total: blocks.length };
}

function classifyBlock(b, key, p33, p66) {
  if (!key || p33 == null) return "inferred";
  const v = b?.[key];
  if (typeof v !== "number" || !Number.isFinite(v)) return "inferred";
  if (v <= p33) return "measured";
  if (v <= p66) return "indicated";
  return "inferred";
}

// Convert azimuth/dip/depth to end point relative to collar
function endPoint(collar, depth) {
  const { x, y, z, azimuth = 0, dip = -90 } = collar;
  const azRad = (azimuth * Math.PI) / 180;
  const dipRad = (dip * Math.PI) / 180;
  const dx = Math.cos(dipRad) * Math.sin(azRad);
  const dy = Math.cos(dipRad) * Math.cos(azRad);
  const dz = Math.sin(dipRad); // negative = downward
  const d = depth || 0;
  return { x2: x + dx * d, y2: y + dy * d, z2: z + dz * d, dir: [dx, dy, dz] };
}

// Unit direction (E, N, Elev) from azimuth+dip
function unitFromAzDip(az, dip) {
  const azR = (az * Math.PI) / 180;
  const dR = (dip * Math.PI) / 180;
  return [Math.cos(dR) * Math.sin(azR), Math.cos(dR) * Math.cos(azR), Math.sin(dR)];
}

/**
 * Minimum-curvature desurveying — mirrors backend `desurvey_minimum_curvature`.
 * Returns [{x, y, z, depth}, ...] polyline stations from collar to EOH.
 */
function buildDesurveyedPath(collar, surveys) {
  const totalDepth = collar.depth || 0;
  const stations = [
    { depth: 0, azimuth: collar.azimuth || 0, dip: collar.dip ?? -90 },
  ];
  (surveys || [])
    .slice()
    .sort((a, b) => (a.depth || 0) - (b.depth || 0))
    .forEach((s) => {
      const d = s.depth || 0;
      if (d <= 0) {
        stations[0] = { depth: 0, azimuth: s.azimuth || 0, dip: s.dip ?? -90 };
      } else {
        stations.push({ depth: d, azimuth: s.azimuth || 0, dip: s.dip ?? -90 });
      }
    });
  if (stations[stations.length - 1].depth < totalDepth) {
    const last = stations[stations.length - 1];
    stations.push({ depth: totalDepth, azimuth: last.azimuth, dip: last.dip });
  }

  let x = collar.x, y = collar.y, z = collar.z;
  const path = [{ x, y, z, depth: 0 }];

  for (let i = 0; i < stations.length - 1; i++) {
    const s1 = stations[i], s2 = stations[i + 1];
    const u1 = unitFromAzDip(s1.azimuth, s1.dip);
    const u2 = unitFromAzDip(s2.azimuth, s2.dip);
    const seg = s2.depth - s1.depth;
    if (seg <= 0) continue;
    let dot = u1[0]*u2[0] + u1[1]*u2[1] + u1[2]*u2[2];
    dot = Math.max(-1, Math.min(1, dot));
    const beta = Math.acos(dot);
    const rf = beta < 1e-6 ? 1.0 : (2.0 / beta) * Math.tan(beta / 2.0);
    const nSteps = Math.max(1, Math.ceil(seg / 2.0));
    let uPrev = u1;
    for (let k = 1; k <= nSteps; k++) {
      const t = k / nSteps;
      const uk = [
        u1[0] + (u2[0] - u1[0]) * t,
        u1[1] + (u2[1] - u1[1]) * t,
        u1[2] + (u2[2] - u1[2]) * t,
      ];
      const sub = seg / nSteps;
      x += (sub / 2.0) * (uPrev[0] + uk[0]) * rf;
      y += (sub / 2.0) * (uPrev[1] + uk[1]) * rf;
      z += (sub / 2.0) * (uPrev[2] + uk[2]) * rf;
      uPrev = uk;
      path.push({ x, y, z, depth: s1.depth + sub * k });
    }
  }
  return path;
}

/**
 * Given a desurveyed polyline, interpolate the 3D point at a given downhole depth.
 * Returns [x, elev, -y] mapped into three.js Y-up coords.
 */
function interpAt(path, depth) {
  if (!path.length) return [0, 0, 0];
  if (depth <= path[0].depth) return [path[0].x, path[0].z, -path[0].y];
  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i], p2 = path[i + 1];
    if (depth <= p2.depth) {
      const t = (depth - p1.depth) / Math.max(1e-6, p2.depth - p1.depth);
      return [
        p1.x + (p2.x - p1.x) * t,
        p1.z + (p2.z - p1.z) * t,
        -(p1.y + (p2.y - p1.y) * t),
      ];
    }
  }
  const last = path[path.length - 1];
  return [last.x, last.z, -last.y];
}

// Compute point along drillhole at given depth
// Uses desurveyed path if surveys are provided; falls back to straight-collar.
function pointAt(collar, depth, path) {
  if (path && path.length > 1) return interpAt(path, depth);
  const { dir } = endPoint(collar, 1);
  return [collar.x + dir[0] * depth, collar.z + dir[2] * depth, -(collar.y + dir[1] * depth)];
}

/**
 * Walk the desurveyed path within [fromDepth, toDepth] and emit an
 * ordered list of Y-up (three.js) points so a caller can render
 * a series of short cylinders that FOLLOW the curved hole instead of
 * cutting a single straight chord between the interval endpoints.
 *
 * • Returns at least 2 points ([from, to]) — matches the pre-curve
 *   behaviour when the hole has no surveys.
 * • With surveys, every path station strictly between `fromDepth` and
 *   `toDepth` becomes an additional bend point.
 */
function sampleCurvedInterval(collar, fromDepth, toDepth, path) {
  const pFrom = pointAt(collar, fromDepth, path);
  const pTo   = pointAt(collar, toDepth,   path);
  if (!path || path.length < 2) return [pFrom, pTo];

  const pts = [pFrom];
  for (let i = 0; i < path.length; i++) {
    const st = path[i];
    if (st.depth > fromDepth + 1e-3 && st.depth < toDepth - 1e-3) {
      pts.push([st.x, st.z, -st.y]);
    }
  }
  pts.push(pTo);
  return pts;
}
// note: three.js uses Y-up. we map (E, N, Elev) -> (x, z, -y)? we'll pick consistent scheme below.

// ────────────────────────────────────────────────────────────────────────
// Iter I-23 · Phase 2 — GPU-instanced drillhole renderer.
//
// Renders every drillhole (collar box + N assay/lithology tubes) as TWO
// InstancedMesh calls total, regardless of how many holes there are:
//   • one instanced Box (all collars, coloured white / highlight cyan)
//   • one instanced Cylinder (all downhole segments, coloured per interval)
//
// Before this, a project with 500 holes × 20 assays produced ~10,000
// individual meshes → ~10,000 draw calls → 8 FPS rotation. Instanced
// versions run at 60 FPS on the same dataset.
//
// The classical `DrillholeMesh` component (below) is kept for the
// highlighted hole so the collar halo + full survey polyline still get
// rendered in full detail on the one hole the user actually inspects.
// ────────────────────────────────────────────────────────────────────────

// Reused temporaries — allocating one Object3D per segment burns GC time.
const _tmpObj = new THREE.Object3D();
const _tmpColor = new THREE.Color();
const _upY = new THREE.Vector3(0, 1, 0);
const _tmpDir = new THREE.Vector3();
const _tmpQuat = new THREE.Quaternion();

export function InstancedDrillholes({ holes, mode, colorCache, skipId, lodTier = "high" }) {
  const collarMesh = useRef();
  const segMesh    = useRef();

  // Segment tier controls cylinder side count. High=8 (default), mid=6, low=4.
  const CYL_SIDES = lodTier === "low" ? 4 : lodTier === "mid" ? 6 : 8;

  // Pre-compute all segments across every hole in a single pass so the
  // instance matrices/colors can be filled synchronously below.
  const segments = useMemo(() => {
    const out = [];
    for (const h of holes || []) {
      if (!h || !h.collar) continue;
      if (h.id === skipId || h.hole_id === skipId) continue;
      const surveys = h.surveys || [];
      const hasSurveys = surveys.length > 0;
      const path = buildDesurveyedPath(h.collar, surveys);
      const intervals = mode === "lithology"
        ? (h.lithology || []).map((l) => ({ from: l.from, to: l.to,
                                            color: litColor(l.lithology, colorCache) }))
        : (h.assays    || []).map((a) => ({ from: a.from, to: a.to,
                                            color: gradeColor(a.grade) }));
      for (const iv of intervals) {
        if (!hasSurveys) {
          // No survey data → single straight cylinder (original behaviour).
          const p1 = pointAt(h.collar, iv.from, path);
          const p2 = pointAt(h.collar, iv.to,   path);
          out.push({ x1: p1[0], y1: p1[1], z1: p1[2],
                     x2: p2[0], y2: p2[1], z2: p2[2], color: iv.color });
        } else {
          // Curved trace — emit one short cylinder per path bend inside [from, to].
          const pts = sampleCurvedInterval(h.collar, iv.from, iv.to, path);
          for (let k = 0; k < pts.length - 1; k++) {
            const a = pts[k], b = pts[k + 1];
            out.push({ x1: a[0], y1: a[1], z1: a[2],
                       x2: b[0], y2: b[1], z2: b[2], color: iv.color });
          }
        }
      }
    }
    return out;
  }, [holes, mode, colorCache, skipId]);

  const collars = useMemo(() => {
    return (holes || [])
      .filter((h) => h && h.collar && h.id !== skipId && h.hole_id !== skipId)
      .map((h) => ({ x: h.collar.x, y: h.collar.z, z: -h.collar.y }));
  }, [holes, skipId]);

  // Populate collar InstancedMesh — one Box3 per collar.
  useEffect(() => {
    const m = collarMesh.current;
    if (!m || !collars.length) return;
    for (let i = 0; i < collars.length; i++) {
      _tmpObj.position.set(collars[i].x, collars[i].y, collars[i].z);
      _tmpObj.rotation.set(0, 0, 0);
      _tmpObj.scale.set(1, 1, 1);
      _tmpObj.updateMatrix();
      m.setMatrixAt(i, _tmpObj.matrix);
      m.setColorAt(i, _tmpColor.set("#00E5FF"));
    }
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    m.count = collars.length;
    m.computeBoundingSphere?.();
  }, [collars]);

  // Populate segment InstancedMesh — one cylinder per interval.
  useEffect(() => {
    const m = segMesh.current;
    if (!m || !segments.length) return;
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      _tmpDir.set(s.x2 - s.x1, s.y2 - s.y1, s.z2 - s.z1);
      const len = Math.max(1e-3, _tmpDir.length());
      _tmpDir.normalize();
      _tmpQuat.setFromUnitVectors(_upY, _tmpDir);
      _tmpObj.position.set(
        (s.x1 + s.x2) / 2,
        (s.y1 + s.y2) / 2,
        (s.z1 + s.z2) / 2,
      );
      _tmpObj.quaternion.copy(_tmpQuat);
      _tmpObj.scale.set(1, len, 1);
      _tmpObj.updateMatrix();
      m.setMatrixAt(i, _tmpObj.matrix);
      m.setColorAt(i, _tmpColor.set(s.color));
    }
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    m.count = segments.length;
    m.computeBoundingSphere?.();
  }, [segments]);

  // Reused per-mesh geometries. Both use `frustumCulled=true` — the
  // instance bounding sphere is populated above so R3F/Three cull the
  // entire batch when the camera looks away from the drill pattern.
  const segGeo = useMemo(() => new THREE.CylinderGeometry(1.4, 1.4, 1, CYL_SIDES, 1, false), [CYL_SIDES]);
  const collarGeo = useMemo(() => new THREE.BoxGeometry(3, 1, 3), []);
  // Free GPU memory when the LOD tier changes / component unmounts.
  useEffect(() => () => { segGeo.dispose(); }, [segGeo]);
  useEffect(() => () => { collarGeo.dispose(); }, [collarGeo]);

  if (!collars.length && !segments.length) return null;

  return (
    <group>
      {collars.length > 0 && (
        <instancedMesh
          ref={collarMesh}
          args={[collarGeo, undefined, collars.length]}
          frustumCulled
        >
          <meshStandardMaterial vertexColors={false} />
        </instancedMesh>
      )}
      {segments.length > 0 && (
        <instancedMesh
          ref={segMesh}
          args={[segGeo, undefined, segments.length]}
          frustumCulled
        >
          <meshStandardMaterial roughness={0.6} metalness={0.1} />
        </instancedMesh>
      )}
    </group>
  );
}

function DrillholeMesh({ hole, mode, colorCache, highlighted = false }) {
  const { collar, lithology = [], assays = [], surveys = [] } = hole;
  const cx = collar.x, cy = collar.y, cz = collar.z, depth = collar.depth || 100;
  const path = useMemo(() => buildDesurveyedPath(collar, surveys), [collar, surveys]);
  const endStation = path[path.length - 1] || { x: cx, y: cy, z: cz };
  const end = { x2: endStation.x, y2: endStation.z, z2: endStation.y ? -endStation.y + endStation.y : endStation.z };
  // Note: endpoint used only for the thin grey path — recompute directly:
  const endPathVec = new THREE.Vector3(endStation.x, endStation.z, -endStation.y);

  // convert (E, N, Elev) -> three (x, y, z) with y = Elev (up)
  const toV = (e, n, elev) => new THREE.Vector3(e, elev, -n);

  const segments = [];
  const hasSurveys = (surveys || []).length > 0;
  const emitInterval = (from, to, color, keyPrefix, idx) => {
    if (!hasSurveys) {
      const p1 = pointAt(collar, from, path);
      const p2 = pointAt(collar, to,   path);
      segments.push({
        key: `${keyPrefix}-${idx}`,
        from: new THREE.Vector3(p1[0], p1[1], p1[2]),
        to:   new THREE.Vector3(p2[0], p2[1], p2[2]),
        color,
      });
      return;
    }
    const pts = sampleCurvedInterval(collar, from, to, path);
    for (let k = 0; k < pts.length - 1; k++) {
      const a = pts[k], b = pts[k + 1];
      segments.push({
        key: `${keyPrefix}-${idx}-${k}`,
        from: new THREE.Vector3(a[0], a[1], a[2]),
        to:   new THREE.Vector3(b[0], b[1], b[2]),
        color,
      });
    }
  };
  if (mode === "lithology" && lithology.length > 0) {
    lithology.forEach((l, i) => emitInterval(l.from, l.to, litColor(l.lithology, colorCache), "lith", i));
  } else if (mode === "grade" && assays.length > 0) {
    assays.forEach((a, i) => emitInterval(a.from, a.to, gradeColor(a.grade), "assay", i));
  }

  // Build a polyline of the full desurveyed path (thin grey line)
  const pathPoints = path.map((p) => new THREE.Vector3(p.x, p.z, -p.y));
  if (pathPoints.length < 2) {
    pathPoints.push(toV(cx, cy, cz));
    pathPoints.push(endPathVec);
  }

  return (
    <group>
      {/* Halo when highlighted from Object Explorer */}
      {highlighted && (
        <mesh position={toV(cx, cy, cz)}>
          <sphereGeometry args={[8, 16, 16]} />
          <meshBasicMaterial color="#22d3ee" transparent opacity={0.35} />
        </mesh>
      )}
      {/* Collar marker */}
      <mesh position={toV(cx, cy, cz)}>
        <boxGeometry args={[3, 1, 3]} />
        <meshStandardMaterial
          color={highlighted ? "#22d3ee" : "#00E5FF"}
          emissive={highlighted ? "#22d3ee" : "#00E5FF"}
          emissiveIntensity={highlighted ? 1.2 : 0.4}
        />
      </mesh>
      {/* Full drillhole path (thin grey polyline — follows survey deviations) */}
      <Line
        points={pathPoints}
        color={highlighted ? "#22d3ee" : "#475569"}
        lineWidth={highlighted ? 3 : 1}
      />
      {/* Colored segments (lithology or grade) rendered as thick tubes */}
      {segments.map((s) => (
        <SegmentTube key={s.key} from={s.from} to={s.to} color={s.color} />
      ))}
    </group>
  );
}

function SegmentTube({ from, to, color }) {
  const geo = useMemo(() => {
    const dir = new THREE.Vector3().subVectors(to, from);
    const length = dir.length();
    const g = new THREE.CylinderGeometry(1.4, 1.4, length, 8, 1, false);
    g.translate(0, length / 2, 0);
    // align cylinder Y with direction
    const quat = new THREE.Quaternion();
    quat.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    g.applyQuaternion(quat);
    g.translate(from.x, from.y, from.z);
    return g;
  }, [from, to]);
  return (
    <mesh geometry={geo}>
      <meshStandardMaterial color={color} roughness={0.6} metalness={0.1} />
    </mesh>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Note (Session I-4): the legacy `BlockCloud` renderer that used to live
// here has been DELETED. It was dead code — never referenced from the
// render tree — but its presence misled agents into believing two block-
// rendering paths existed. The ONE and ONLY block renderer is `BlockMeshes`
// below. Do not re-introduce a second block renderer without deleting
// this comment first.
// ────────────────────────────────────────────────────────────────────────

/**
 * BlockMeshes — GPU-instanced block model renderer.
 *
 * Uses THREE.InstancedMesh so millions of blocks can be drawn in a SINGLE
 * draw call instead of one draw per box. Per-instance colours are wired
 * via `setColorAt()` which populates `instanceColor`; three.js then
 * automatically enables the `USE_INSTANCING_COLOR` shader define.
 *
 * IMPORTANT — the material MUST NOT set `vertexColors: true`. That
 * enables `USE_COLOR` which makes the shader multiply by the geometry's
 * per-vertex `color` attribute; BoxGeometry has no such attribute so
 * WebGL returns (0,0,0) and 0 × instanceColor = solid black. This was
 * the "3D viewport black" regression reported in Iter 47.
 *
 * LOD (level-of-detail): when the block count exceeds `maxRender`, we
 * apply a uniform-stride decimation once (never inside the render loop).
 * There is NO camera-distance-based LOD any more — see Session I-4 fix
 * notes below on why the previous `useFrame` LOD was a footgun.
 */
export function BlockMeshes({ blocks, cutoff, maxRender = 250_000, blockSize = null, onBlockClick = null, highlightIndex = -1, colorMode = "grade", classification = null }) {
  const meshRef = useRef();

  // Filter to ore blocks only (waste is drawn separately if enabled).
  const ore = useMemo(
    () => blocks.filter((b) => (b.grade ?? 0) >= cutoff),
    [blocks, cutoff],
  );

  // Server-side stride is preferable but we cap client-side too so a
  // caller that forgets to pass ?stride= doesn't crash the browser.
  const sample = useMemo(() => {
    if (ore.length <= maxRender) return ore;
    const step = Math.ceil(ore.length / maxRender);
    return ore.filter((_, i) => i % step === 0);
  }, [ore, maxRender]);

  // Session I · fix — Auto-scale the grade colour ramp to the actual grade
  // range in this specific block model. Previously the ramp was hard-coded
  // for a 0-2 % Cu/Au range, so an Fe deposit with grades in the 30-40 %
  // range was clamped to t = 1.0 for every block → every instance rendered
  // identical solid red, giving the illusion of a single smooth hull rather
  // than 38k individually shaded blocks. Compute {min, max} on the p5/p95
  // percentiles of the actual sample so outliers don't flatten the ramp.
  const gradeRange = useMemo(() => {
    if (sample.length === 0) return { min: 0, max: 2 };
    const gs = sample
      .map((b) => (typeof b.grade === "number" && Number.isFinite(b.grade)) ? b.grade : null)
      .filter((g) => g !== null)
      .sort((a, b) => a - b);
    if (gs.length === 0) return { min: 0, max: 2 };
    // p5 / p95 clamp so a handful of outlier ultra-high-grade blocks don't
    // shrink the visible ramp for the bulk of the population.
    const p5  = gs[Math.floor(gs.length * 0.05)];
    const p95 = gs[Math.floor(gs.length * 0.95)];
    const min = Math.min(p5, cutoff ?? 0);       // never crop below cutoff
    const max = Math.max(p95, min + 0.001);      // guard against min == max
    return { min, max };
  }, [sample, cutoff]);

  const geo = useMemo(() => {
    const g = new THREE.BoxGeometry(1, 1, 1);
    // Phase 2 — BVH accelerator so onClick/onContextMenu picking scales
    // from O(n) per-triangle raycast to O(log n) tree descent. Critical
    // for enterprise datasets (500k+ blocks) where the default raycaster
    // freezes the tab for hundreds of ms per click.
    g.computeBoundsTree?.();
    return g;
  }, []);
  // Iter 47 · Black-viewport fix — MeshBasicMaterial is unlit so every
  // block cube renders at the FULL grade-ramp colour regardless of camera
  // angle or scene lights. The previous Lambert material dropped the
  // colour to near-black on faces pointing away from the two directional
  // lights, which is exactly the "3D viewport black" regression users
  // reported. Cubes don't need shading to be legible in a technical viewer;
  // the grade ramp is the signal.
  //
  // Base color explicitly white and toneMapped disabled — the shader
  // multiplies base × instanceColor, and modern R3F sets the renderer to
  // ACESFilmic tone mapping which was crushing our hue-saturation-lightness
  // grade-ramp swatches into darkness. `toneMapped:false` bypasses that.
  const material = useMemo(
    () => new THREE.MeshBasicMaterial({
      // Iter 47 · Session A — DO NOT set `vertexColors: true` here.
      // three.js enables `USE_INSTANCING_COLOR` automatically whenever an
      // InstancedMesh has a non-null `instanceColor` buffer (which we
      // populate via setColorAt below), and that is the ONLY thing that
      // makes per-block colours appear. Setting `vertexColors: true`
      // ADDITIONALLY enables `USE_COLOR`, which makes the shader multiply
      // by the geometry's per-vertex `color` attribute — but BoxGeometry
      // has no such attribute, so WebGL returns (0,0,0) for it and
      // 0 × instanceColor collapses every block to solid black. That is
      // the exact "3D viewport black" regression users reported.
      color: 0xffffff,
      vertexColors: false,
      transparent: false,
      opacity: 1.0,
      toneMapped: false,
    }),
    [],
  );

  // Upload per-instance transforms + colours once, whenever the sample changes.
  useEffect(() => {
    if (!meshRef.current || sample.length === 0) return;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const c = new THREE.Color();
    for (let i = 0; i < sample.length; i++) {
      const b = sample[i];
      // V4.0 Sprint 5 · Tiny-model fix — block size is on the parent
      // BlockModel doc as `block_size`, NOT per-block. Fall back through
      // (a) explicit per-block size (b) parent block_size (c) 1 as last resort.
      const s = b.size || blockSize || 1;
      m.compose(
        new THREE.Vector3(b.x, b.z, -b.y),
        q, new THREE.Vector3(s, s, s),
      );
      meshRef.current.setMatrixAt(i, m);
      // Highlight glow for selected block
      if (i === highlightIndex) {
        c.set("#22d3ee"); // cyan highlight
      } else if (colorMode === "classification") {
        const cls = classifyBlock(b, classification?.key, classification?.p33, classification?.p66);
        c.set(CLASSIFICATION_COLORS[cls] || CLASSIFICATION_COLORS.inferred);
      } else {
        c.set(gradeColor(b.grade, gradeRange.min, gradeRange.max));
      }
      meshRef.current.setColorAt(i, c);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
    if (meshRef.current.instanceColor) {
      meshRef.current.instanceColor.needsUpdate = true;
    }
    meshRef.current.count = sample.length;
    meshRef.current.frustumCulled = false;
  }, [sample, blockSize, highlightIndex, gradeRange, colorMode, classification]);

  // Session I-4 fix — the previous camera-distance LOD silently dropped
  // `instancedMesh.count` to 50 % or 25 % once the camera exceeded
  // `500 * blockSize` units from origin. On a 15 m-block UTM model that
  // threshold (~7500 m) is crossed at moderate zoom, leaving only 9 600
  // of 38 399 blocks drawn — which reads as "scattered sticks" against
  // the drillhole traces (exactly one of the three failure modes the user
  // reported). Modern desktop WebGL renders 38 k instanced cubes in a
  // single draw call at 60 fps; the LOD was solving a non-existent
  // performance problem. Removed. If we ever need LOD again for
  // multi-million-block deposits, gate it on `sample.length > 500_000`
  // AND base the distance on the camera-to-target vector, NOT
  // `camera.position.length()` (which is meaningless once the scene has
  // been shifted to origin via the sceneOffset group).
  //
  // For the tiny model case (< 5000 blocks) we DO keep the count locked
  // to sample.length so the initial mount doesn't render an empty scene
  // when the useEffect above races the first frame.
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.count = sample.length;
    }
  }, [sample.length]);
  // Publish the actual on-screen instance count so the scene HUD can
  // surface it — makes regressions like the LOD footgun visually obvious.
  useEffect(() => {
    try { window.__smin_blocks_rendered = sample.length; } catch { /* ignore */ }
  }, [sample.length]);

  const handleClick = (e) => {
    if (!onBlockClick) return;
    e.stopPropagation();
    const idx = e.instanceId;
    if (idx == null || idx < 0 || idx >= sample.length) return;
    // Right-click == edit; left-click == select
    onBlockClick(sample[idx], idx, e.nativeEvent);
  };

  if (sample.length === 0) return null;
  return (
    <instancedMesh
      ref={meshRef}
      args={[geo, material, sample.length]}
      onContextMenu={handleClick}
      onClick={handleClick}
    >
      {/* Material is baked at args construction — MUST NOT be overridden
          as a child, because R3F sets flags like vertexColors AFTER shader
          compile, which leaves the shader ignoring instanceColor entirely.
          See DrillholeViewer3D history Iter 47 - Session A. */}
    </instancedMesh>
  );
}

export function PitShell({ shell }) {
  const safe = shell && shell.center ? shell : null;
  const center = safe?.center ?? [0, 0, 0];
  const radius_top = safe?.radius_top ?? 200;
  const radius_bottom = safe?.radius_bottom ?? 60;
  const depth = safe?.depth ?? 150;
  const geo = useMemo(() => {
    const g = new THREE.CylinderGeometry(radius_top, radius_bottom, depth, 24, 1, true);
    return g;
  }, [radius_top, radius_bottom, depth]);
  if (!safe) return null;
  return (
    <mesh geometry={geo} position={[center[0], center[2] - depth / 2, -center[1]]}>
      <meshStandardMaterial
        color="#FF5722"
        transparent
        opacity={0.15}
        side={THREE.DoubleSide}
        wireframe={false}
      />
    </mesh>
  );
}

/**
 * ExtrapolationMeshes — grey semi-transparent InstancedMesh for blocks that
 * fall OUTSIDE the drilled envelope. Renders only ore-grade blocks (>= cutoff)
 * so the viewer isn't drowned in waste; caps at 200k blocks to keep frame
 * budgets sane (matches BlockMeshes' `maxRender` default).
 *
 * Uses THREE.MeshBasicMaterial with `toneMapped: false` for the same reasons
 * documented on BlockMeshes: instancing shader compatibility + ACES tone
 * mapping was crushing dim greys to black in prior iterations.
 */
function ExtrapolationMeshes({ blocks, cutoff, maxRender = 200_000, blockSize = null }) {
  const meshRef = useRef();
  const ore = useMemo(
    () => blocks.filter((b) => (b.grade ?? 0) >= cutoff),
    [blocks, cutoff],
  );
  const sample = useMemo(() => {
    if (ore.length <= maxRender) return ore;
    const step = Math.ceil(ore.length / maxRender);
    return ore.filter((_, i) => i % step === 0);
  }, [ore, maxRender]);

  const geo = useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);
  const material = useMemo(
    () => new THREE.MeshBasicMaterial({
      color: "#4B5563",
      transparent: true,
      opacity: 0.28,
      toneMapped: false,
    }),
    [],
  );

  useEffect(() => {
    if (!meshRef.current || sample.length === 0) return;
    const dummy = new THREE.Object3D();
    for (let i = 0; i < sample.length; i += 1) {
      const b = sample[i];
      const s = blockSize || b.size || 10;
      dummy.position.set(b.x, b.z, -b.y);
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    }
    meshRef.current.instanceMatrix.needsUpdate = true;
    meshRef.current.count = sample.length;
  }, [sample, blockSize]);

  if (sample.length === 0) return null;
  return (
    <instancedMesh
      ref={meshRef}
      args={[geo, material, sample.length]}
      data-testid="extrapolation-mesh"
    />
  );
}

function VariogramEllipsoid({ ellipsoid }) {
  const geo = useMemo(() => new THREE.SphereGeometry(1, 32, 24), []);
  if (!ellipsoid || !ellipsoid.center) return null;
  const { center, range, sill, nugget, azimuth = 0, dip = 0, anisotropy = 1 } = ellipsoid;
  const anis = Math.max(0.1, anisotropy);
  const rMajor = range;
  const rMinor = range / anis;
  const rZ = range / anis;
  // Center: (E,N,elev) -> (x=E, y=elev, z=-N)
  const pos = [center[0], center[2], -center[1]];
  // Azimuth is measured from North (Y-axis) clockwise. In three's XZ-plane
  // (X=East, Z=-North), an azimuth θ from North becomes a rotation of -θ
  // around the world Y-up axis (right-hand rule).
  const azRad = -(azimuth * Math.PI) / 180;
  const dipRad = (dip * Math.PI) / 180;
  const nuggetFrac = Math.min(1, Math.max(0, (nugget || 0) / Math.max(sill || 1, 0.0001)));
  return (
    <group position={pos} rotation={[0, azRad, 0]}>
      <group rotation={[dipRad, 0, 0]}>
        {/* Outer ellipsoid (range envelope) */}
        <mesh geometry={geo} scale={[rMajor, rZ, rMinor]}>
          <meshStandardMaterial
            color="#00E5FF"
            transparent
            opacity={0.14}
            side={THREE.DoubleSide}
            depthWrite={false}
            roughness={0.7}
          />
        </mesh>
        {/* Wireframe outline for clarity */}
        <mesh geometry={geo} scale={[rMajor, rZ, rMinor]}>
          <meshBasicMaterial color="#00E5FF" wireframe transparent opacity={0.35} depthWrite={false} />
        </mesh>
        {/* Nugget core — small inner sphere sized by nugget/sill ratio */}
        {nuggetFrac > 0 && (
          <mesh geometry={geo} scale={[rMajor * nuggetFrac, rZ * nuggetFrac, rMinor * nuggetFrac]}>
            <meshBasicMaterial color="#FF5722" transparent opacity={0.28} depthWrite={false} />
          </mesh>
        )}
        {/* Principal axes as line rays */}
        <Line points={[[-rMajor, 0, 0], [rMajor, 0, 0]]} color="#F1F5F9" lineWidth={1} transparent opacity={0.7} />
        <Line points={[[0, -rZ, 0], [0, rZ, 0]]} color="#F1F5F9" lineWidth={1} transparent opacity={0.5} />
        <Line points={[[0, 0, -rMinor], [0, 0, rMinor]]} color="#F1F5F9" lineWidth={1} transparent opacity={0.5} />
      </group>
    </group>
  );
}

function AxesLegend({ extents }) {
  const size = extents ?? 200;
  return (
    <group>
      <Grid args={[size * 2, size * 2]} cellColor="#272A35" sectionColor="#3B3F4A" fadeDistance={size * 3} infiniteGrid={false} />
    </group>
  );
}

export default function DrillholeViewer3D({
  drillholes = [],
  mode = "lithology",
  blockModel = null,
  cutoff = 0.3,
  pitShell = null,
  surfaces = [],
  topography = null,
  ellipsoid = null,
  envelope = null,                // { effective_radius_m, ... } — Session I-37 P10
                                   // Blocks within `effective_radius_m` of any
                                   // drillhole collar render normally; blocks
                                   // outside render as a semi-transparent grey
                                   // extrapolation-zone layer.
  showExtrapolationZone = true,   // Iter I-38 · P11 — Pit KPI card in
                                   // MinePlanTab can hide the grey layer for
                                   // cleaner investor screenshots.
  colorMode = "grade",             // Iter I-38 · P12 — "grade" (default) or
                                   // "classification" (Measured / Indicated /
                                   // Inferred tercile of kriging variance).
  height = "100%",
  hideInternalControls = false,   // Iter 32 · V2 · hides the internal zoom
                                   // toolbar & HUD when the enterprise viewer
                                   // wraps this component with its own chrome.
  projectId = null,               // needed for the branded 3D snapshot PDF
  projectMeta = null,             // { name, code, commodity } for title block
  onBlockClick = null,            // (block, index, nativeEvent) — for in-viewer editing
  highlightBlockIndex = -1,       // sync 3D highlight with Object Explorer selection
}) {
  const colorCache = useMemo(() => ({}), [drillholes]);
  const workspace = useWorkspaceSafe();
  const wrapperRef = useRef(null);
  const [highlightedId, setHighlightedId] = useState(null);

  // Listen for global select-object events from the Object Explorer
  useEffect(() => {
    const onSelect = (e) => {
      const det = e.detail || {};
      // Match by drillhole kind (id + kind)
      if (det.kind === "drillholes") setHighlightedId(det.id);
      // Auto-clear after 5 seconds
      setTimeout(() => setHighlightedId(null), 5000);
    };
    window.addEventListener("smin:select-object", onSelect);
    return () => window.removeEventListener("smin:select-object", onSelect);
  }, []);

  // ---- Scene extent (bounding sphere) ---------------------------------
  // Compute a robust bounding sphere across drillholes + block model +
  // surfaces so the camera can auto-fit regardless of dataset kind.
  // This runs once per data change — never inside the render loop.
  const sceneInfo = useMemo(() => {
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    let count = 0;
    const consider = (x, y, z) => {
      if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
      minX = Math.min(minX, x); minY = Math.min(minY, y); minZ = Math.min(minZ, z);
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); maxZ = Math.max(maxZ, z);
      count += 1;
    };
    for (const h of drillholes) {
      const c = h.collar || {};
      consider(c.x, c.y, c.z);
      // also consider bottom of hole for extent
      if (typeof c.depth === "number") consider(c.x, c.y, (c.z || 0) - c.depth);
    }
    if (blockModel && Array.isArray(blockModel.blocks)) {
      for (const b of blockModel.blocks) consider(b.x, b.y, b.z);
    }
    for (const s of surfaces) {
      if (!s?.vertices) continue;
      for (let i = 0; i < s.vertices.length; i += 3) {
        consider(s.vertices[i], s.vertices[i + 1], s.vertices[i + 2]);
      }
    }
    if (count === 0) return { center: [0, 0, 0], radius: 100, valid: false };
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2;
    const dx = maxX - minX, dy = maxY - minY, dz = maxZ - minZ;
    // V4.0 Sprint 5 · Tiny-model fix — pad the sphere by one block's worth
    // so the auto-fit view shows the full block-model volume (not just the
    // centre points). Falls back gracefully when no block model is loaded.
    const blockPad = blockModel?.block_size ? Number(blockModel.block_size) : 0;
    const radius = Math.max(50, Math.sqrt(dx * dx + dy * dy + dz * dz) / 2 + blockPad);
    return { center: [cx, cy, cz], radius, valid: true,
             extent: { minX, minY, minZ, maxX, maxY, maxZ } };
  }, [drillholes, blockModel, surfaces]);

  // Floating origin — shift the entire scene by `-center` so we render
  // near (0,0,0). Fixes z-fighting / camera-lost issues for UTM projects
  // where raw coords are in the millions.
  const sceneOffset = useMemo(() => {
    const [cx, cy, cz] = sceneInfo.center;
    // World convention: X=east, Y=elevation (project Z), Z=-north (project -Y)
    return [-cx, -cz, cy];
  }, [sceneInfo]);

  // Camera pose relative to the shifted scene (target = 0,0,0)
  const camDistance = Math.max(200, sceneInfo.radius * 2.6);
  const camPosition = [camDistance * 0.9, camDistance * 0.7, camDistance * 0.9];
  const target = [0, 0, 0];
  const controlsRef = useRef();
  const [zoomTick, setZoomTick] = useState(null);
  const [history, setHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [historyPose, setHistoryPose] = useState(null);
  const [renderTick, setRenderTick] = useState(0); // used to force auto-fit on data change

  // Auto-fit whenever the underlying data changes (never leaves the user
  // staring at a blank scene). Debounced via requestAnimationFrame so we
  // fit AFTER the meshes have mounted this frame.
  useEffect(() => {
    if (!sceneInfo.valid) return;
    const raf = requestAnimationFrame(() => {
      setZoomTick({ kind: "fit", n: Date.now() });
      setRenderTick((t) => t + 1);
    });
    return () => cancelAnimationFrame(raf);
  }, [sceneInfo.valid, sceneInfo.radius,
      sceneInfo.center[0], sceneInfo.center[1], sceneInfo.center[2]]);

  const bump = (kind, extra = {}) => setZoomTick({ kind, n: Date.now(), ...extra });

  const commitPose = (pose) => {
    // Truncate any forward history when a new action lands
    setHistory((prev) => {
      const truncated = prev.slice(0, histIdx + 1);
      truncated.push(pose);
      // cap to 20 entries
      const next = truncated.slice(-20);
      setHistIdx(next.length - 1);
      return next;
    });
  };

  const goPrev = () => {
    if (histIdx <= 0) return;
    const idx = histIdx - 1;
    setHistIdx(idx);
    setHistoryPose({ ...history[idx], _n: Date.now() });
  };
  const goNext = () => {
    if (histIdx >= history.length - 1) return;
    const idx = histIdx + 1;
    setHistIdx(idx);
    setHistoryPose({ ...history[idx], _n: Date.now() });
  };

  // -------- 3D snapshot export (PNG · JPG · branded PDF) ---------------
  // Grabs the WebGL back-buffer via canvas.toDataURL — works because we
  // set preserveDrawingBuffer:true on the Canvas above. For PDF we round-trip
  // the PNG through a branded backend endpoint so we always get a proper A4
  // landscape sheet with the project title block.
  const captureCanvas = (mime, quality) => {
    const canvas = wrapperRef.current?.querySelector("canvas");
    if (!canvas) throw new Error("3D canvas not ready — try again in a moment.");
    return canvas.toDataURL(mime, quality);
  };
  const downloadFile = (dataUrl, ext) => {
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
    const proj = (projectMeta?.code || "SMIN").replace(/[^A-Za-z0-9_-]/g, "_");
    a.href = dataUrl;
    a.download = `${proj}_3D_snapshot_${stamp}.${ext}`;
    a.click();
  };
  const snapshotPng = () => {
    try { downloadFile(captureCanvas("image/png"), "png"); toast.success("3D snapshot saved as PNG"); }
    catch (e) { toast.error(e.message); }
  };
  const snapshotJpg = () => {
    try { downloadFile(captureCanvas("image/jpeg", 0.92), "jpg"); toast.success("3D snapshot saved as JPG"); }
    catch (e) { toast.error(e.message); }
  };
  const snapshotPdf = async () => {
    if (!projectId) {
      // Fallback — if the viewer wasn't given a projectId (e.g. embedded
      // preview), just wrap the PNG client-side by opening it in a print
      // dialog. Not as pretty as the branded PDF but never fails.
      const dataUrl = captureCanvas("image/png");
      const w = window.open("");
      if (!w) { toast.error("Popup blocked — allow popups to print PDF"); return; }
      w.document.write(`<img src="${dataUrl}" style="width:100%"/>`);
      w.print();
      return;
    }
    try {
      const dataUrl = captureCanvas("image/png");
      const token = localStorage.getItem("smin_token");
      const r = await fetch(`${API_BASE}/projects/${projectId}/exports/3d-snapshot.pdf`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          image_base64: dataUrl.split(",")[1],
          view_meta: {
            drillholes: drillholes.length,
            blocks: (blockModel?.blocks || []).length,
            surfaces: surfaces.length,
            mode,
          },
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      const proj = (projectMeta?.code || "SMIN").replace(/[^A-Za-z0-9_-]/g, "_");
      a.href = url;
      a.download = `${proj}_3D_snapshot_${stamp}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("3D snapshot saved as branded PDF");
    } catch (e) {
      toast.error(`PDF snapshot failed: ${e.message}`);
    }
  };

  // Phase 1 — auto-degrade for heavy scenes: turn off MSAA above 100k blocks
  // so the render loop keeps up. Users rarely notice a jaggie on a 200k-block
  // model — they notice the 8 FPS.
  const heavyScene = (blockModel?.blocks?.length || 0) > 100000;

  // Iter I-38 · P12 — pre-compute the classification terciles once on the
  // FULL block set (before envelope partitioning) so the legend counts stay
  // consistent regardless of whether extrapolation-zone blocks are shown.
  const classification = useMemo(() => {
    if (colorMode !== "classification") return null;
    return computeClassificationTerciles(blockModel?.blocks || []);
  }, [colorMode, blockModel?.blocks]);

  return (
    <div ref={wrapperRef}
      style={{ height, width: "100%", background: "#050608", position: "relative" }}>
      <Canvas
        key={renderTick /* force fresh WebGL context if user hits reset-scene */}
        // Phase 1 core win — stop the render loop when nothing changes.
        // OrbitControls, `invalidate()` calls on data changes, and drei's
        // AdaptiveDpr / AdaptiveEvents all trigger fresh frames on demand.
        frameloop="demand"
        // Cap DPR — 4K/Retina otherwise renders at 3× native and eats FPS.
        dpr={[1, 2]}
        // Give R3F the freedom to lean into performance mode while the user
        // is interacting; it snaps back to full quality when the scene idles.
        performance={{ min: 0.5, max: 1, debounce: 200 }}
        camera={{ position: camPosition, fov: 45,
                  near: Math.max(0.1, camDistance / 5000),
                  far: Math.max(1e7, camDistance * 100) }}
        gl={{ logarithmicDepthBuffer: true,
              antialias: !heavyScene,
              powerPreference: "high-performance",
              // Required so the WebGL back-buffer survives long enough for
              // canvas.toDataURL() to capture a real screenshot (see the
              // 3D snapshot export button on the toolbar).
              preserveDrawingBuffer: true }}
      >
        <AdaptiveDpr pixelated={false} />
        <AdaptiveEvents />
        <color attach="background" args={["#050608"]} />
        <ambientLight intensity={0.75} />
        <directionalLight position={[200, 300, 150]} intensity={1.1} />
        <directionalLight position={[-200, 100, -150]} intensity={0.5} />
        <AxesLegend extents={Math.max(300, sceneInfo.radius * 0.5)} />
        {/* Floating origin: render everything shifted so the model sits
            around (0,0,0). Prevents z-fighting on large UTM coordinates. */}
        <group position={sceneOffset}>
          {/* Iter I-23 · Phase 2 — GPU-instanced drillhole batch.
              Renders every hole as two InstancedMesh draw calls total.
              The one highlighted hole is *additionally* rendered via
              the classical DrillholeMesh below so the halo + survey
              polyline still appear in full detail. */}
          <InstancedDrillholes
            holes={drillholes}
            mode={mode}
            colorCache={colorCache}
            skipId={highlightedId || null}
            lodTier={
              (drillholes?.length || 0) > 1000 ? "low" :
              (drillholes?.length || 0) >  300 ? "mid" : "high"
            }
          />
          {highlightedId && drillholes
            .filter((h) => h.id === highlightedId || h.hole_id === highlightedId)
            .map((h) => (
              <DrillholeMesh
                key={h.id || h.hole_id}
                hole={h}
                mode={mode}
                colorCache={colorCache}
                highlighted
              />
            ))}
          {blockModel && (() => {
            // Session I-37 · P10 — partition blocks into inside / outside the
            // drilled envelope so extrapolation-zone blocks can be flagged in
            // grey. If no envelope was supplied, render every block normally.
            const allBlocks = blockModel.blocks || [];
            const envRadius = envelope ? Number(envelope.effective_radius_m || 0) : 0;
            if (!envRadius) {
              return (
                <BlockMeshes
                  blocks={allBlocks}
                  cutoff={cutoff}
                  blockSize={blockModel.block_size || null}
                  onBlockClick={onBlockClick}
                  highlightIndex={highlightBlockIndex}
                  colorMode={colorMode}
                  classification={classification}
                />
              );
            }
            const collars = drillholes
              .map((d) => d.collar)
              .filter((c) => c && typeof c.x === "number");
            const r2 = envRadius * envRadius;
            const inside = [];
            const outside = [];
            for (const b of allBlocks) {
              let within = false;
              for (const c of collars) {
                const dx = b.x - c.x;
                const dy = b.y - c.y;
                if (dx * dx + dy * dy <= r2) { within = true; break; }
              }
              (within ? inside : outside).push(b);
            }
            return (
              <>
                <BlockMeshes
                  blocks={inside}
                  cutoff={cutoff}
                  blockSize={blockModel.block_size || null}
                  onBlockClick={onBlockClick}
                  highlightIndex={highlightBlockIndex}
                  colorMode={colorMode}
                  classification={classification}
                />
                {showExtrapolationZone && (
                  <ExtrapolationMeshes
                    blocks={outside}
                    cutoff={cutoff}
                    blockSize={blockModel.block_size || null}
                  />
                )}
              </>
            );
          })()}
          {pitShell && <PitShell shell={pitShell} />}
          {ellipsoid && <VariogramEllipsoid ellipsoid={ellipsoid} />}
          {surfaces.map((s) => (
            <TriMesh key={s.id || s.name}
              vertices={s.vertices} faces={s.faces}
              color={s.color || "#FBBF24"} opacity={0.55} />
          ))}
          {topography && topography.vertices && topography.faces && (
            <TriMesh vertices={topography.vertices} faces={topography.faces}
              color="#475569" opacity={0.4} wireframe={false} />
          )}
        </group>
        <OrbitControls ref={controlsRef} target={target} makeDefault
          // Phase 1 · smooth camera + inertia.
          enableDamping dampingFactor={0.08}
          // Prevent unnecessary re-renders by trimming pan/zoom sensitivity
          // to physically-nice defaults.
          rotateSpeed={0.9}
          zoomSpeed={0.85}
          panSpeed={0.9}
          // Screen-space panning keeps the target sensible on rotated scenes.
          screenSpacePanning
        />
        <AutoFitOnLoad controlsRef={controlsRef} radius={sceneInfo.radius}
          center={sceneInfo.center} sceneOffset={sceneOffset} />
        <ZoomController controlsRef={controlsRef} action={zoomTick} target={target}
          onPose={commitPose} historyPose={historyPose} />
        <CoordReader onCoord={workspace?.setCoord} sceneCenter={sceneInfo.center} />
        <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
          <GizmoViewport axisColors={["#FF5722", "#00E5FF", "#FBBF24"]} labelColor="#F8FAFC" />
        </GizmoHelper>
      </Canvas>
      {/* Iter I-38 · P12 — Classification legend overlay (top-right).
          Only visible in classification mode. Shows the JORC-style tercile
          split (Measured / Indicated / Inferred) with block counts + %. */}
      {colorMode === "classification" && classification && (
        <div
          className="absolute top-3 right-3 z-20 bg-neutral-950/85 backdrop-blur border border-neutral-800 rounded-md px-3 py-2 min-w-[180px]"
          data-testid="viewer3d-classification-legend"
        >
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">
            Classification · terciles
          </div>
          {["measured", "indicated", "inferred"].map((cls) => {
            const n = classification.counts?.[cls] || 0;
            const pct = classification.total ? (n / classification.total) * 100 : 0;
            const label = cls.charAt(0).toUpperCase() + cls.slice(1);
            return (
              <div
                key={cls}
                className="flex items-center justify-between gap-2 font-mono text-[11px] py-0.5"
                data-testid={`viewer3d-classification-row-${cls}`}
              >
                <span className="flex items-center gap-2 text-neutral-200">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ background: CLASSIFICATION_COLORS[cls] }}
                  />
                  {label}
                </span>
                <span className="text-neutral-400 tabular-nums">
                  {n.toLocaleString()} · {pct.toFixed(1)}%
                </span>
              </div>
            );
          })}
          <div className="mt-1.5 pt-1.5 border-t border-neutral-800/60 font-mono text-[9.5px] text-neutral-500">
            {classification.key
              ? <>src: <span className="text-neutral-300">{classification.key}</span></>
              : "no variance data · all Inferred"}
          </div>
        </div>
      )}
      <ZoomToolbar
        onIn={() => bump("in")}
        onOut={() => bump("out")}
        onReset={() => bump("reset")}
        onFit={() => bump("fit")}
        onView={(v) => bump("view", { view: v })}
        onPrev={goPrev}
        onNext={goNext}
        canPrev={histIdx > 0}
        canNext={histIdx < history.length - 1}
        hidden={hideInternalControls}
      />
      {/* Scale bar overlay — bottom-left */}
      <div className={`absolute bottom-4 left-4 z-10 pointer-events-none ${hideInternalControls ? "hidden" : ""}`}>
        <ScaleBar controlsRef={controlsRef} />
      </div>
      {/* Scene diagnostics HUD — bottom-right corner. */}
      <div className={`absolute bottom-4 right-4 z-10 flex items-center gap-2 ${hideInternalControls ? "hidden" : ""}`}
        data-testid="viewer3d-scene-hud">
        <div className="bg-card/90 border border-border px-3 py-1.5 backdrop-blur font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {sceneInfo.valid ? (
            <>
              Ext&nbsp;{Math.round(sceneInfo.radius * 2)}m · Holes {drillholes.length} ·
              {blockModel ? ` Blk ${(blockModel.blocks || []).length} ·` : ""}
              {surfaces.length ? ` Surf ${surfaces.length} ·` : ""}
              {" "}Ctr&nbsp;{sceneInfo.center.map((v) => Math.round(v)).join(",")}
            </>
          ) : (
            <>Empty scene — no geometry to display</>
          )}
        </div>
        <button
          onClick={() => { bump("fit"); setRenderTick((t) => t + 1); }}
          data-testid="viewer3d-recover"
          title="Recover scene (recalculate bounds, reset camera, rebuild WebGL context)"
          className="h-8 px-3 border border-border bg-card/90 hover:bg-primary hover:text-primary-foreground font-mono text-[10px] uppercase tracking-widest transition-colors"
        >
          Recover
        </button>
      </div>

      {/* 3D snapshot export toolbar — top-LEFT so it doesn't collide with
          the cross-section panel that lives at top-right. Rendered as a
          single connected pill for a professional grouped-button look. */}
      <div className={`absolute top-4 left-4 z-10 ${hideInternalControls ? "hidden" : ""}`}
           data-testid="viewer3d-snapshot-toolbar">
        <div className="inline-flex items-stretch border border-border bg-card/95 backdrop-blur shadow-lg divide-x divide-border">
          <div className="flex items-center gap-1.5 px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest text-muted-foreground bg-background/40">
            <Camera className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
            Snapshot
          </div>
          <button
            onClick={snapshotPng}
            data-testid="viewer3d-snapshot-png"
            title="Download high-resolution PNG"
            className="flex items-center gap-1.5 px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <FileImage className="h-3.5 w-3.5" strokeWidth={2} /> PNG
          </button>
          <button
            onClick={snapshotJpg}
            data-testid="viewer3d-snapshot-jpg"
            title="Download JPG (smaller file)"
            className="flex items-center gap-1.5 px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <FileImage className="h-3.5 w-3.5" strokeWidth={2} /> JPG
          </button>
          <button
            onClick={snapshotPdf}
            data-testid="viewer3d-snapshot-pdf"
            title="Download branded A4 PDF"
            className="flex items-center gap-1.5 px-3 py-1.5 font-heading text-[10px] uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={2} /> PDF
          </button>
        </div>
      </div>
    </div>
  );
}

/** Read pointer-move in world space and push to the workspace context so the
    Status Bar can render live X/Y/Z (in project CRS). Adds `sceneCenter`
    back so the user sees the ORIGINAL project coordinates (UTM etc.) even
    though the scene is rendered around a local origin. */
/**
 * AutoFitOnLoad — V4.0 Sprint 5 · Tiny-model fix.
 * When the scene bounding radius grows significantly (blocks streaming in
 * or drillholes finishing load), reposition the camera + orbit target so
 * the entire model is framed. Prevents the "tiny model in the corner"
 * complaint when blocks arrive after the initial camera set-up.
 */
function AutoFitOnLoad({ controlsRef, radius, center, sceneOffset }) {
  const { camera } = useThree();
  const lastRadiusRef = useRef(0);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!controlsRef.current) return;
    if (!radius || radius <= 0) return;
    const last = lastRadiusRef.current;
    // Auto-refit whenever:
    //   • First time we have valid data (last was 0), OR
    //   • Scene grew ≥30 % since last fit (blocks streamed in), OR
    //   • Scene shrank ≥50 % (deleted a huge dataset)
    // Also always re-fit the first 3 times we see a change, in case blocks
    // stream in in multiple chunks and the first fit lands on partial data.
    const changed = last === 0 ||
                    radius > last * 1.3 ||
                    radius < last * 0.5;
    const forceEarly = attemptsRef.current < 3;
    if (!changed && !forceEarly) return;
    attemptsRef.current += 1;

    const d = Math.max(200, radius * 2.6);
    camera.position.set(d * 0.9, d * 0.7, d * 0.9);
    camera.near = Math.max(0.1, d / 5000);
    camera.far  = Math.max(1e7, d * 100);
    camera.updateProjectionMatrix();
    if (controlsRef.current) {
      // After the floating-origin group shift, the scene sits around (0,0,0).
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
    lastRadiusRef.current = radius;
  }, [radius, controlsRef, camera]);

  return null;
}



function CoordReader({ onCoord, sceneCenter }) {
  const { camera, gl, scene } = useThree();
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const plane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  const centerRef = useRef(sceneCenter || [0, 0, 0]);
  useEffect(() => { centerRef.current = sceneCenter || [0, 0, 0]; }, [sceneCenter]);
  useEffect(() => {
    if (!onCoord) return;
    const el = gl.domElement;
    const point = new THREE.Vector3();
    const ndc = new THREE.Vector2();
    let raf = null;
    const handler = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const rect = el.getBoundingClientRect();
        ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(ndc, camera);
        const meshes = [];
        scene.traverse((o) => { if (o.isMesh) meshes.push(o); });
        const hits = raycaster.intersectObjects(meshes, false);
        if (hits.length) {
          point.copy(hits[0].point);
        } else if (!raycaster.ray.intersectPlane(plane, point)) {
          return;
        }
        // World→CRS: rendered X→+east, Y→elevation, Z→-north
        // Add sceneCenter back so the user sees the original UTM coords.
        const [cx, cy, cz] = centerRef.current;
        onCoord({
          x: point.x + cx,
          y: -point.z + cy,
          z: point.y + cz,
        });
      });
    };
    const clear = () => onCoord(null);
    el.addEventListener("pointermove", handler);
    el.addEventListener("pointerleave", clear);
    return () => {
      el.removeEventListener("pointermove", handler);
      el.removeEventListener("pointerleave", clear);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [camera, gl, scene, raycaster, plane, onCoord]);
  return null;
}

/** Simple scale bar — computes screen distance per world unit at the target. */
function ScaleBar({ controlsRef }) {
  const [meters, setMeters] = useState(100);
  useEffect(() => {
    const tick = () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const dist = controls.getDistance ? controls.getDistance()
        : controls.object.position.distanceTo(controls.target);
      // heuristic: pick a "round" scale so the bar stays around 100–150px on screen
      const desired = dist * 0.15;
      const pow = Math.pow(10, Math.floor(Math.log10(desired || 1)));
      const rounded = Math.max(1, Math.round(desired / pow) * pow);
      setMeters(rounded);
    };
    const t = setInterval(tick, 500);
    tick();
    return () => clearInterval(t);
  }, [controlsRef]);
  return (
    <div className="flex items-center gap-2 bg-card/85 border border-border px-2 py-1 backdrop-blur"
      data-testid="viewer3d-scalebar">
      <div className="h-1 bg-foreground/60 relative" style={{ width: 120 }}>
        <div className="absolute left-0 top-0 h-2 w-px bg-foreground" />
        <div className="absolute right-0 top-0 h-2 w-px bg-foreground" />
      </div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {meters >= 1000 ? `${(meters/1000).toFixed(1)} km` : `${meters} m`}
      </span>
    </div>
  );
}

/** Access workspace context if available; returns null when the viewer is
    rendered outside the shell (e.g. public share page). */
function useWorkspaceSafe() {
  return useWorkspace();
}

// Small overlay panel with zoom buttons — placed on top-right of the 3D viewport.
/**
 * Reusable icon-button + CSS-only tooltip. The tooltip renders on hover
 * WITHOUT needing a TooltipProvider higher up the tree — this component
 * has to work anywhere it's dropped in (Block Model tab, Mine Plan, Geo
 * Modelling, cross-sections). Extra `data-tip-side` lets adjacent buttons
 * push their tooltip to the correct edge so it never clips off-screen.
 */
function ToolbarButton({
  onClick, disabled = false, "data-testid": testid, tip,
  variant = "icon",             // "icon" (h-9 w-9) | "chip" (h-7 px-2)
  tipSide = "left",             // preferred side of the tooltip
  children,
}) {
  const base =
    "relative grid place-items-center rounded-md border border-border/70 " +
    "bg-card/85 backdrop-blur hover:bg-primary hover:text-primary-foreground " +
    "hover:border-primary hover:ring-1 hover:ring-primary/40 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary " +
    "transition-all duration-150 group";
  const size = variant === "chip"
    ? "h-7 px-2 font-mono text-[10px] uppercase tracking-widest"
    : "h-9 w-9";
  const dis = disabled ? "opacity-40 cursor-not-allowed pointer-events-none" : "";
  const tipPos = {
    left:  "right-full mr-2 top-1/2 -translate-y-1/2",
    right: "left-full  ml-2 top-1/2 -translate-y-1/2",
    top:   "bottom-full mb-2 left-1/2 -translate-x-1/2",
    bottom:"top-full mt-2 left-1/2 -translate-x-1/2",
  }[tipSide] || "right-full mr-2 top-1/2 -translate-y-1/2";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      // Keep native `title` too so the accessibility tree + copy-paste
      // parity remains for users who prefer OS tooltips.
      title={tip}
      className={`${base} ${size} ${dis}`}
    >
      {children}
      {tip && (
        <span
          role="tooltip"
          className={`pointer-events-none absolute ${tipPos} z-30 px-2 py-1 ` +
                     "rounded-md bg-primary text-primary-foreground " +
                     "text-[10.5px] font-mono uppercase tracking-widest whitespace-nowrap " +
                     "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 " +
                     "translate-x-0 transition-opacity duration-150 shadow-md"}>
          {tip}
        </span>
      )}
    </button>
  );
}

function ZoomToolbar({ onIn, onOut, onReset, onFit, onView, onPrev, onNext, canPrev, canNext, hidden = false }) {
  // Session I-4 · Sprint J — Full rail redesign.
  //  · `top-14` clears the MultiViewport layout switcher (which lives at
  //    `top-2` in the parent). Previously the ZoomToolbar sat at `top-3`
  //    and the two toolbars visually collided at their edges regardless
  //    of viewport size. 14 · 4 = 56 px of headroom is enough at every
  //    breakpoint tested (720p through 4k) with the layout-switcher pill
  //    measuring ~34 px tall.
  //  · Three visually-separated groups (Camera / Orientation / History)
  //    with a subtle `border-t border-border/40` divider between them.
  //  · Every icon button carries a proper CSS tooltip via `ToolbarButton`
  //    that opens immediately on hover — no 700 ms OS delay.
  //  · Zoom in / out consolidated into a horizontal +/- pair to reduce
  //    vertical stack height (freeing space for future toolbar groups).
  //  · The old duplicate "Home view" button has been removed — both it
  //    and "Reset camera" called `onReset`, doing exactly the same thing.
  //    We keep the RotateCcw icon labelled "Reset view".
  const wrapperClass = hidden
    ? "absolute pointer-events-none opacity-0 -left-[9999px]"
    : "absolute top-14 right-3 z-10 flex flex-col rounded-lg " +
      "border border-border/60 bg-card/70 backdrop-blur shadow-lg " +
      "p-1.5 gap-1.5";
  return (
    <div className={wrapperClass} data-testid="viewer3d-zoom-toolbar" aria-hidden={hidden}>
      {/* ── Group 1 · Camera controls ─────────────────────────────── */}
      <div className="flex flex-col gap-1" data-testid="viewer3d-zoom-group-camera">
        {/* Zoom in / out compacted into a single horizontal pair */}
        <div className="flex gap-1">
          <ToolbarButton onClick={onIn}  data-testid="viewer3d-zoom-in"  tip="Zoom in">
            <ZoomIn className="h-4 w-4" strokeWidth={1.75} />
          </ToolbarButton>
          <ToolbarButton onClick={onOut} data-testid="viewer3d-zoom-out" tip="Zoom out">
            <ZoomOut className="h-4 w-4" strokeWidth={1.75} />
          </ToolbarButton>
        </div>
        <ToolbarButton onClick={onFit}   data-testid="viewer3d-zoom-fit"   tip="Fit to screen (F)">
          <Maximize2 className="h-4 w-4" strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton onClick={onReset} data-testid="viewer3d-zoom-reset" tip="Reset view">
          <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
        </ToolbarButton>
      </div>

      {/* ── Group 2 · Orientation presets ─────────────────────────── */}
      <div className="pt-1.5 border-t border-border/40 flex flex-col gap-1"
           data-testid="viewer3d-zoom-group-orientation">
        {[
          { k: "top", l: "TOP", tip: "Top view (looking down)" },
          { k: "bottom", l: "BOT", tip: "Bottom view (looking up)" },
          { k: "front", l: "FR",  tip: "Front view" },
          { k: "back",  l: "BK",  tip: "Back view" },
          { k: "left",  l: "L",   tip: "Left view" },
          { k: "right", l: "R",   tip: "Right view" },
          { k: "iso",   l: "ISO", tip: "Isometric view" },
        ].map((v) => (
          <ToolbarButton key={v.k}
            variant="chip"
            data-testid={`viewer3d-view-${v.k}`}
            onClick={() => onView?.(v.k)}
            tip={v.tip}>
            {v.l}
          </ToolbarButton>
        ))}
      </div>

      {/* ── Group 3 · View history ────────────────────────────────── */}
      <div className="pt-1.5 border-t border-border/40 flex gap-1"
           data-testid="viewer3d-zoom-group-history">
        <ToolbarButton
          onClick={onPrev} disabled={!canPrev}
          data-testid="viewer3d-prev-view"
          tip="Undo last view change">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        </ToolbarButton>
        <ToolbarButton
          onClick={onNext} disabled={!canNext}
          data-testid="viewer3d-next-view"
          tip="Redo view change">
          <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
        </ToolbarButton>
      </div>
    </div>
  );
}

// Lives inside the Canvas — reads a bumped action from parent state and dollies
// the camera toward / away from the OrbitControls target. `reset` restores the
// default camera pose; `fit` frames the whole scene bounding sphere; view
// presets (top/bottom/front/back/left/right/iso) fly the camera to
// orthographic-style presets around the scene centre.
function ZoomController({ controlsRef, action, target, onPose, historyPose }) {
  const { camera, scene } = useThree();
  const initialPose = useRef(null);

  const applyPose = (pose) => {
    const controls = controlsRef.current;
    if (!controls) return;
    camera.position.copy(pose.position);
    controls.target.copy(pose.target);
    camera.near = Math.max(0.1, pose.position.distanceTo(pose.target) / 1000);
    camera.far = Math.max(camera.far, pose.position.distanceTo(pose.target) * 20);
    camera.updateProjectionMatrix();
    controls.update();
  };

  // Fly to a pose from the history stack (previous/next buttons).
  useEffect(() => {
    if (!historyPose) return;
    applyPose(historyPose);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPose]);

  useEffect(() => {
    // Capture the very first camera pose so `reset` can restore it.
    if (!initialPose.current) {
      initialPose.current = {
        position: camera.position.clone(),
        target: new THREE.Vector3(...target),
      };
    }
  }, [camera, target]);

  useEffect(() => {
    if (!action || !controlsRef.current) return;
    const controls = controlsRef.current;
    const cam = camera;
    const tgt = controls.target;

    const commitPose = () => {
      onPose?.({ position: cam.position.clone(), target: tgt.clone() });
    };

    if (action.kind === "in" || action.kind === "out") {
      const factor = action.kind === "in" ? 0.8 : 1.25;
      const dir = cam.position.clone().sub(tgt);
      dir.multiplyScalar(factor);
      cam.position.copy(tgt.clone().add(dir));
      controls.update();
      commitPose();
    } else if (action.kind === "reset" && initialPose.current) {
      cam.position.copy(initialPose.current.position);
      controls.target.copy(initialPose.current.target);
      controls.update();
      commitPose();
    } else if (action.kind === "view") {
      // Compute a preset camera position based on the current scene bounding box.
      const box = new THREE.Box3();
      let counted = 0;
      scene.traverse((obj) => {
        if (!obj.isMesh && !obj.isLine && !obj.isPoints) return;
        const typeName = obj.type || "";
        if (typeName.includes("Helper") || typeName.includes("Grid")) return;
        if (!obj.geometry) return;
        box.expandByObject(obj);
        counted += 1;
      });
      if (!counted || box.isEmpty()) return;
      const size = box.getSize(new THREE.Vector3());
      const centerBox = box.getCenter(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z) || 100;
      const fov = (cam.fov * Math.PI) / 180;
      const dist = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;
      const preset = action.view;
      const cx = centerBox.x, cy = centerBox.y, cz = centerBox.z;
      let pos;
      // Our world convention: X→east, Y→elevation, Z→-north
      switch (preset) {
        case "top":    pos = new THREE.Vector3(cx, cy + dist, cz + 0.01); break;
        case "bottom": pos = new THREE.Vector3(cx, cy - dist, cz + 0.01); break;
        case "front":  pos = new THREE.Vector3(cx, cy, cz + dist); break;   // looking north
        case "back":   pos = new THREE.Vector3(cx, cy, cz - dist); break;
        case "left":   pos = new THREE.Vector3(cx - dist, cy, cz); break;
        case "right":  pos = new THREE.Vector3(cx + dist, cy, cz); break;
        case "iso":
        default:       pos = new THREE.Vector3(cx + dist * 0.7, cy + dist * 0.7, cz + dist * 0.7); break;
      }
      cam.position.copy(pos);
      controls.target.copy(centerBox);
      cam.near = Math.max(0.1, dist / 1000);
      cam.far = Math.max(cam.far, dist * 20);
      cam.updateProjectionMatrix();
      controls.update();
      commitPose();
    } else if (action.kind === "fit") {
      // Bound only the meaningful meshes — exclude the Grid helper, gizmo overlays and lights
      const box = new THREE.Box3();
      let counted = 0;
      scene.traverse((obj) => {
        if (!obj.isMesh && !obj.isLine && !obj.isPoints) return;
        // Skip helpers (grid, axes, gizmo)
        const typeName = obj.type || "";
        if (typeName.includes("Helper") || typeName.includes("Grid")) return;
        // Skip if geometry has no bounding info
        if (!obj.geometry) return;
        box.expandByObject(obj);
        counted += 1;
      });
      if (counted && !box.isEmpty()) {
        const size = box.getSize(new THREE.Vector3());
        const centerBox = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 100;
        const fov = (cam.fov * Math.PI) / 180;
        const dist = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;
        const dir = cam.position.clone().sub(controls.target).normalize();
        if (dir.lengthSq() < 1e-6) dir.set(1, 1, 1).normalize();
        cam.position.copy(centerBox.clone().add(dir.multiplyScalar(dist)));
        controls.target.copy(centerBox);
        // Ensure the far clip is big enough
        cam.far = Math.max(cam.far, dist * 8);
        cam.updateProjectionMatrix();
        controls.update();
        commitPose();
      }
    }
  }, [action, camera, controlsRef, scene]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fit on FIRST render once meshes are mounted (fixes wrong initial camera for UTM scenes)
  useEffect(() => {
    if (!controlsRef.current) return;
    let attempts = 0;
    const maxAttempts = 20;   // 20 × 200ms = 4s total budget
    const tryFit = () => {
      const controls = controlsRef.current;
      if (!controls) return;
      const box = new THREE.Box3();
      let counted = 0;
      scene.traverse((obj) => {
        if (!obj.isMesh && !obj.isLine && !obj.isPoints) return;
        const typeName = obj.type || "";
        if (typeName.includes("Helper") || typeName.includes("Grid")) return;
        if (!obj.geometry) return;
        box.expandByObject(obj);
        counted += 1;
      });
      if (counted && !box.isEmpty()) {
        const size = box.getSize(new THREE.Vector3());
        const centerBox = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 100;
        const fov = (camera.fov * Math.PI) / 180;
        const dist = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;
        camera.position.set(
          centerBox.x + dist * 0.7,
          centerBox.y + dist * 0.7,
          centerBox.z + dist * 0.7,
        );
        controls.target.copy(centerBox);
        camera.near = Math.max(0.1, dist / 1000);
        camera.far = Math.max(camera.far, dist * 20);
        camera.updateProjectionMatrix();
        controls.update();
        initialPose.current = {
          position: camera.position.clone(),
          target: centerBox.clone(),
        };
        return true;
      }
      return false;
    };
    const interval = setInterval(() => {
      attempts += 1;
      if (tryFit() || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function TriMesh({ vertices, faces, color = "#FBBF24", opacity = 0.55, wireframe = false }) {
  // Validate — skip invalid geometry rather than crashing the renderer.
  const geo = useMemo(() => {
    if (!vertices || !faces || vertices.length < 9 || faces.length < 3) return null;
    if (vertices.length % 3 !== 0) return null;
    const g = new THREE.BufferGeometry();
    // Remap (E,N,elev) -> (x=E, y=elev, z=-N) to match viewer scheme
    const remapped = new Float32Array(vertices.length);
    let anyBad = false;
    for (let i = 0; i < vertices.length; i += 3) {
      const e = vertices[i], n = vertices[i + 1], el = vertices[i + 2];
      if (!Number.isFinite(e) || !Number.isFinite(n) || !Number.isFinite(el)) {
        anyBad = true;
        break;
      }
      remapped[i] = e; remapped[i + 1] = el; remapped[i + 2] = -n;
    }
    if (anyBad) return null;
    g.setAttribute("position", new THREE.BufferAttribute(remapped, 3));
    g.setIndex(new THREE.BufferAttribute(new Uint32Array(faces), 1));
    g.computeVertexNormals();
    g.computeBoundingBox();
    g.computeBoundingSphere();
    return g;
  }, [vertices, faces]);
  // Phase 1 — free GPU memory when the surface is unmounted / swapped.
  // (Hook lives before any conditional return per rules-of-hooks.)
  useEffect(() => () => { if (geo) geo.dispose(); }, [geo]);
  if (!geo) return null;
  return (
    <mesh geometry={geo}>
      <meshStandardMaterial
        color={color}
        transparent
        opacity={opacity}
        side={THREE.DoubleSide}
        wireframe={wireframe}
        roughness={0.6}
        metalness={0.05}
      />
    </mesh>
  );
}
